if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        if (this._hasChangeListener) {
          return;
        }

        this._hasChangeListener = true;
        const combinations = this.querySelector('[data-kgy-available-combinations]');
        if (combinations) {
          this.kgyCombinations = JSON.parse(combinations.textContent);
          const committed = JSON.parse(this.querySelector('[data-selected-variant]').textContent);
          this.kgyInitGroups();
          this.kgyNormalize(committed?.available ? committed.options : undefined);
          const values = this.kgyValues();
          if (!committed?.available || values.some((value, i) => value !== committed.options[i])) {
            queueMicrotask(() => { if (this.isConnected) (this.querySelector('select, input:checked'))?.dispatchEvent(new Event('change', { bubbles: true })); });
          }
        }

        this.addEventListener(
          'change',
          (event) => {
            // Check if target is inside this element
            const targetInThisElement = this.contains(event.target);

            // Check if there's a nested variant-selects between this and target
            let hasNestedVariantSelects = false;
            let currentElement = event.target;

            while (currentElement && currentElement !== document.body) {
              if (currentElement.tagName === 'VARIANT-SELECTS') {
                if (currentElement !== this) {
                  hasNestedVariantSelects = true;
                  break;
                } else {
                  break;
                }
              }
              currentElement = currentElement.parentElement;
            }

            // Ignore if target is not in this element or there's a nested variant-selects
            if (!targetInThisElement || hasNestedVariantSelects) {
              return;
            }

            if (this.kgyCombinations) this.kgyNormalize();
            const target = this.getInputForEventTarget(event.target);
            this.updateSelectedSwatchValue(event);
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            });
          },
          true
        ); // Use CAPTURE phase to catch events before they bubble
      }

      // Cache original nodes, including inert options, to preserve swatches and fast-variant metadata.
      kgyInitGroups() {
        this.kgyGroups = Array.from(this.querySelectorAll('.product-form__input')).filter(group => group.closest('variant-selects') === this).map(group => {
          const select = group.querySelector('select');
          const entries = [];
          const collect = (node, root) => {
            if (node.matches('template')) {
              node.content.querySelectorAll('option, input[type="radio"]').forEach(input => collect(input, node.content));
            } else {
              const label = select ? null : Array.from(root.querySelectorAll('label')).find(label => label.htmlFor === node.id);
              entries.push({ input: node, label });
            }
          };
          group.querySelectorAll('option, input[type="radio"], template[data-kgy-option]').forEach(node => collect(node, group));
          group.querySelectorAll('template[data-kgy-option]').forEach(node => node.remove());
          return { group, select, entries };
        });
      }

      kgyValues() {
        return this.kgyGroups.map(({ select, entries }) => select ? select.value : entries.find(({input}) => input.checked)?.input.value);
      }

      kgyNormalize(restoreValues) {
        if (!this.kgyGroups?.length || !this.kgyCombinations?.variants.length) return;
        const wanted = restoreValues || this.kgyValues();
        const prefix = [];
        this.kgyGroups.forEach(({group, select, entries}, index) => {
          const allowed = new Set(this.kgyCombinations.variants.filter(v => prefix.every((value, i) => v[i] === value)).map(v => v[index]));
          const options = entries.filter(({input}) => allowed.has(input.value));
          const choice = allowed.has(wanted[index]) ? wanted[index] : options[0]?.input.value;
          if (choice === undefined) return;
          entries.forEach(({input, label}) => {
            input.remove();
            if (label) label.remove();
            input.disabled = !allowed.has(input.value);
            input.classList.toggle('disabled', input.disabled);
            if (select) {
              input.textContent = input.value;
              input.selected = input.value === choice;
              input.toggleAttribute('selected', input.selected);
            } else {
              input.checked = input.value === choice;
              input.toggleAttribute('checked', input.checked);
            }
          });
          options.forEach(({input, label}) => {
            (select || group).append(input);
            if (label) { label.querySelectorAll('.label-unavailable').forEach(el => el.remove()); group.append(label); }
          });
          if (select) select.value = choice;
          prefix.push(choice);
          const label = group.querySelector('[data-selected-swatch-value]');
          if (label) label.textContent = choice;
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.hasAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
